import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, IMAGES } from "../lib/data";
import api, { formatApiErrorDetail } from "../lib/api";
import { getVisitorId, getAttribution } from "../lib/track";
import { Reveal } from "../components/site/Reveal";

import { Seo } from "../components/site/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", job_title: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", { ...form, visitor_id: getVisitorId(), attribution: getAttribution() });
      setSent(true);
      toast.success("Message sent", { description: data.message });
    } catch (err) {
      toast.error("Something went wrong", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-[72px]">
      <Seo title="Contact Us" description="Talk with the OneHarvest team about absorbents, water treatment, ground protection, and spill response. Call (713) 725-7413 or request a quote." />
      <section className="bg-ink relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-10" />
        <div className="container-x py-20 relative">
          <div className="kicker text-flame mb-4">Contact</div>
          <h1 className="font-display font-black text-white text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
            Talk to our Solutions Team.
          </h1>
          <p className="mt-5 text-white/70 text-lg max-w-2xl">
            Tell us about your operation. We'll respond fast — usually within one business day.
          </p>
        </div>
      </section>

      <section className="bg-background">
        <div className="container-x py-24 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-6">
              {[
                { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
                { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
                { icon: MapPin, label: "Headquarters", value: COMPANY.address },
                { icon: Clock, label: "Hours", value: COMPANY.hours },
              ].map((c) => (
                <div key={c.label} className="group flex items-start gap-4 border border-border bg-card p-6 hover:border-flame/50 transition-colors">
                  <div className="h-11 w-11 bg-flame/10 border border-flame/25 text-flame flex items-center justify-center flex-shrink-0 group-hover:bg-flame group-hover:text-white transition-colors duration-300">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="kicker text-muted-foreground">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} className="mt-1 block font-display font-semibold text-ink hover:text-flame">{c.value}</a>
                    ) : (
                      <div className="mt-1 font-display font-semibold text-ink">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
              <div className="border border-border overflow-hidden">
                <img src={IMAGES.crew} alt="Our team" className="w-full h-48 object-cover" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            {sent ? (
              <div data-testid="contact-success" className="border border-border bg-card p-12 text-center">
                <CheckCircle2 className="h-14 w-14 text-flame mx-auto" />
                <h2 className="mt-6 font-display font-bold text-2xl text-ink">Message received.</h2>
                <p className="mt-3 text-muted-foreground">Thank you — our team will reach out shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="border border-border bg-card p-8 md:p-10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name *" testid="contact-name" value={form.name} onChange={set("name")} required />
                  <Field label="Email *" testid="contact-email" type="email" value={form.email} onChange={set("email")} required />
                  <Field label="Company" testid="contact-company" value={form.company} onChange={set("company")} />
                  <Field label="Phone" testid="contact-phone" value={form.phone} onChange={set("phone")} />
                  <div className="sm:col-span-2">
                    <label className="kicker text-charcoal block mb-2">Job title / role</label>
                    <select data-testid="contact-job-title" value={form.job_title} onChange={set("job_title")}
                      className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame">
                      <option value="">Select role</option>
                      {["Owner / Executive", "Safety / HSE Director", "Procurement / Purchasing", "Operations Manager", "Superintendent / Foreman", "Engineer", "Fleet / Asset Manager", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="kicker text-charcoal block mb-2">Message *</label>
                  <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")}
                    className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame resize-none" />
                </div>
                <button data-testid="contact-submit" disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : "Send Message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="kicker text-charcoal block mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} required={required}
        className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame" />
    </div>
  );
}
